import React, {
  Fragment,
  FunctionComponent,
  MutableRefObject,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react'
import { FieldRenderProps } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import {
  NativeSyntheticEvent,
  Platform,
  ReturnKeyTypeOptions,
  TextInput as TextInputComponent,
  TextInputContentSizeChangeEventData,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'

import { BottomSheetTextInput as BottomSheetTextInputComponent } from '@gorhom/bottom-sheet'
import { isNumber } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { InputLabelBadgeTypes } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'

import { AppContext } from '../../context'
import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { useFocusState } from '../../hooks/useFocusState'
import { BottomSheetParentContext } from '../../molecules/bottomSheet/BottomSheet'
import { getFocusBoxShadow, getInputFieldStyles } from '../../styles/commonStyles'
import { BodyTextSize, getFontStyles } from '../../styles/typeStyles'
import { getAuroraInputProps } from '../../utils/themes/auroraInput'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { deviceIsSamsung } from '../../utils/utils'
import { AiLinearGradient } from '../aiLinearGradient/AiLinearGradient'
import { BodyText } from '../bodyText/BodyText'
import { BaseInput } from '../formElements/BaseInput'
import { TooltipMaxWidth, ToolTipTriggerConfig } from '../tooltip/Tooltip'

export interface InputFieldProps extends Omit<TextInputProps, 'autoComplete'> {
  label?: string
  sublabel?: string
  subLabelComponent?: string
  error?: string
  name?: string
  readOnly?: boolean
  active?: boolean
  typeStyle?: Dict
  secureTextEntry?: boolean
  placeholderTextColor?: string
  backgroundColor?: string
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoComplete?: TextInputProps['autoComplete'] | 'country-name'
  baseInputStyle?: ViewStyle
  toolTipContent?: string | ReactElement
  maxCharLength?: number
  showCharLimit?: boolean
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipSize?: TooltipMaxWidth
  accessibilityLabelledBy?: string
  scrollEnabled?: boolean
  labelStyle?: ViewStyle
  labelContainerStyle?: ViewStyle
  isOptionalLabel?: boolean // show "OPTIONAL" tag next to the label
  inputContainerStyle?: ViewStyle
  toolTipContentStyle?: { container?: Dict }
  toolTipHoverEnabled?: boolean
  showErrorAsWarning?: boolean
  areAllInputRefsSet?: boolean
  inputExtras?: React.ReactElement
  navigateToNewField?: (direction: 'previous' | 'next') => void
  getReturnKeyType?: (fieldName: string) => 'next' | 'done' | 'previous'
  setInputRefs?: ({ name, ref }: { name: string; ref: MutableRefObject<any> }) => void
  labelBadgeType?: InputLabelBadgeTypes
  nativeID?: string
  leftIcon?: ReactElement
  aiBorder?: boolean
  customTextStyle?: TextStyle
  errorAccessibilityText?: string
  required?: boolean
  showFocusRing?: boolean
}

const InputFieldContainer = styled.View<{
  theme: ThemeType
  error?: string
  backgroundColor?: string
  enableScroll?: CSSObject
  showErrorAsWarning?: boolean
  readOnly?: boolean
  textInputStyles?: CSSObject
}>(({ theme: { colors }, error, backgroundColor, enableScroll, showErrorAsWarning, readOnly, textInputStyles }) => ({
  flexDirection: 'row',
  height: 'auto',
  ...(textInputStyles && { ...textInputStyles }),
  ...(!readOnly &&
    (showErrorAsWarning || error) && {
      borderColor: showErrorAsWarning ? colors.borderWarning : colors.inputOutlineError,
    }),
  ...(backgroundColor && { backgroundColor }),
  ...enableScroll,
}))

const ReactTextInput = styled(TextInputComponent)`
  ${Platform.select({ web: `outline-style: none;` })};
`
const BottomSheetTextInput = styled(BottomSheetTextInputComponent)`
  ${Platform.select({ web: `outline-style: none;` })};
`

const CharacterCounterContainer = styled(BodyText)<{
  theme: ThemeType
}>(({ theme: { colors } }) => ({
  position: 'absolute',
  right: '16px',
  bottom: '12px',
  color: colors.inputTextPlaceholder,
}))

export const useAutoFillHack = () => {
  // https://github.com/react-native-elements/react-native-elements/issues/2998
  /** THIS IS A TERRIBLE HACK TO MAKE AUTOFILL WORK ON ANDROID!!! */
  const [autoFill, setAutoFill] = useState(false)

  useEffect(() => {
    setAutoFill(true)
  }, [])

  return Platform.select({
    android: autoFill,
    ios: true,
    web: true,
  })
}

export const InputField: FunctionComponent<InputFieldProps> = ({
  label,
  sublabel,
  subLabelComponent,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  active,
  multiline,
  numberOfLines = 1,
  error,
  name,
  readOnly,
  keyboardType,
  inputAccessoryViewID,
  placeholderTextColor,
  backgroundColor,
  autoCapitalize = 'sentences',
  autoComplete,
  secureTextEntry,
  typeStyle,
  baseInputStyle,
  toolTipContent,
  maxCharLength,
  showCharLimit,
  toolTipTriggerConfig,
  toolTipSize,
  accessibilityLabelledBy,
  scrollEnabled = false,
  children,
  testID,
  // 'default' returnKeyType is only available for IOS and the default equivalent for android is 'done
  returnKeyType = Platform.OS === 'ios' ? 'default' : 'done',
  inputContainerStyle,
  labelStyle,
  labelContainerStyle,
  isOptionalLabel,
  textContentType,
  toolTipContentStyle,
  toolTipHoverEnabled,
  showErrorAsWarning,
  areAllInputRefsSet,
  navigateToNewField,
  setInputRefs,
  getReturnKeyType,
  autoFocus,
  blurOnSubmit,
  inputExtras,
  labelBadgeType,
  nativeID,
  leftIcon,
  onSubmitEditing,
  aiBorder,
  customTextStyle,
  errorAccessibilityText,
  accessibilityLabel: accessibilityLabelProp,
  required,
  showFocusRing,
}) => {
  const { isFocused, focusProps } = useFocusState({ onFocus, onBlur, active })
  const isActive = isFocused

  const { colors, isDarkMode, fontFamilyMetadata, componentStyles, isAuroraTheme } = useTheme()
  const textInputStyles = getInputFieldStyles({
    colors,
    fontFamilyMetadata,
    componentStyles,
    active: isActive && !readOnly,
    readOnly,
    error: !!error,
  }) as CSSObject
  const [focusRef] = useAccessibilityFocus({ active: isActive, delay: 200 })
  const { lineHeight, ...prunedTypeStyle } = typeStyle || getFontStyles(colors, fontFamilyMetadata).body.default
  const inputLineHeight = isNumber(lineHeight) ? lineHeight : 0
  const additionalHeight = multiline ? Platform.select({ ios: 5, default: 0 }) : 0
  const initialContentHeight = inputLineHeight * numberOfLines + additionalHeight
  const [contentHeight, setContentHeight] = useState(initialContentHeight)
  const { copyPasteDisabled } = useContext(AppContext)
  const isSamsung = deviceIsSamsung()
  const autoFill = useAutoFillHack()
  const isParentBottomSheet = useContext(BottomSheetParentContext)

  // Submitted answers are read in full far more often than a few lines at a time, so a
  // read-only box renders its whole answer rather than staying at `numberOfLines`.
  // See the render below.
  const readOnlyMultilineNative = !!readOnly && !!multiline && Platform.OS !== 'web'

  // Force scroll for multiline on native so users can reach all of their content.
  // With scrollEnabled={false} iOS binds the UITextView's text container to its frame:
  // onContentSizeChange stops firing past the initial size, so the box can neither grow
  // nor scroll and anything past `numberOfLines` is clipped and unreachable. Scrolling
  // also keeps the caret visible while typing, since UITextView tracks it automatically.
  const effectiveScrollEnabled = scrollEnabled || (!!multiline && Platform.OS !== 'web')

  const handleOnContentSizeChange = (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
    const contentSize = event.nativeEvent.contentSize.height
    /* possible outcomes:
     * fixed height for the textbox WITH scrollable content
     * dynamic height for the textbox WITHOUT scrollable content
     */
    if (!effectiveScrollEnabled)
      setContentHeight(contentSize > initialContentHeight ? contentSize : initialContentHeight)
  }

  // this logic enables user to navigate to InputField from keyboard(on mobile devices)
  useEffect(() => {
    if (setInputRefs && focusRef && name) {
      setInputRefs({ name, ref: focusRef })
    }
  }, [focusRef, name, setInputRefs])

  const [currentReturnKeyType, setCurrentReturnKeyType] = useState<ReturnKeyTypeOptions | undefined>(returnKeyType)

  // if keyboard form navigation is enabled, we want to make sure to update the returnKeyType after all the input refs have been set
  // since the array of navigable fields will be completed and we can determine if the user can navigate to next field.
  useEffect(() => {
    if (areAllInputRefsSet && name) {
      const updatedReturnKeyType = getReturnKeyType ? getReturnKeyType(name) : returnKeyType
      setCurrentReturnKeyType(updatedReturnKeyType)
    }
  }, [areAllInputRefsSet, getReturnKeyType, name, returnKeyType])

  // Generate errorMessageId for proper aria-describedby association
  const errorMessageId = name ? `${name}-error-message` : undefined

  // Generate labelNativeID for proper accessibilityLabelledBy association
  const labelNativeID = name ? `${name}-label` : undefined

  // Set accessibilityLabelledBy to reference the label container's nativeID if label exists and accessibilityLabelledBy is not already provided
  const finalAccessibilityLabelledBy = accessibilityLabelledBy || (label && labelNativeID ? labelNativeID : undefined)

  const TextInput = isParentBottomSheet ? BottomSheetTextInput : ReactTextInput
  const useAiBorder = aiBorder && componentStyles.inputField.enableAiBorder
  const BorderComponent = useAiBorder ? AiLinearGradient : Fragment

  return (
    <BaseInput
      label={label}
      labelStyle={labelStyle}
      labelContainerStyle={labelContainerStyle}
      isOptionalLabel={isOptionalLabel}
      subLabel={sublabel}
      subLabelComponent={subLabelComponent}
      error={error}
      name={name}
      style={baseInputStyle}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipContentStyle={toolTipContentStyle}
      toolTipHoverEnabled={toolTipHoverEnabled}
      toolTipSize={toolTipSize}
      showErrorAsWarning={showErrorAsWarning}
      labelBadgeType={labelBadgeType}
      errorMessageId={errorMessageId}
      labelNativeID={labelNativeID}
      customTextStyle={customTextStyle}
      errorAccessibilityText={errorAccessibilityText}
      required={required}
    >
      <BorderComponent {...(useAiBorder ? { style: { padding: 1, borderRadius: 16 } } : {})}>
        <InputFieldContainer
          backgroundColor={backgroundColor}
          error={error}
          style={[
            useAiBorder ? { borderColor: 'transparent', borderRadius: 15, padding: 12, height: 112 } : {},
            inputContainerStyle,
            isActive && showFocusRing ? getFocusBoxShadow({ colors }) : {},
          ]}
          showErrorAsWarning={showErrorAsWarning}
          readOnly={readOnly}
          textInputStyles={textInputStyles}
        >
          {leftIcon && <View style={{ flexShrink: 0 }}>{leftIcon}</View>}
          {!autoFill ? (
            <></>
          ) : readOnlyMultilineNative ? (
            /**
             * A submitted answer is content, not an input, so render it as text at its full
             * natural height and let the enclosing form body scroll. A read-only TextInput
             * can't self-size on native anyway (its onContentSizeChange doesn't report usable
             * content height when editable={false}).
             *
             * Deliberately unbounded: capping the height here would give every answer its own
             * nested scroll region, so a page with several answers would carve the screen into
             * competing scroll areas. One scrolling body handles any number of fields of any
             * size. Screen readers also announce this as text rather than an editable field.
             */
            <View style={{ flexGrow: 1, flexShrink: 1 }} testID={testID ?? name}>
              {/* BodyText keeps long-press to select and copy, already gated on copyPasteDisabled. */}
              <BodyText
                text={value}
                color={textInputStyles?.color as string}
                style={{ lineHeight: inputLineHeight, minHeight: initialContentHeight }}
                accessibilityLabel={accessibilityLabelProp ?? label}
                accessibilityLabelledBy={finalAccessibilityLabelledBy}
              />
            </View>
          ) : (
            <TextInput
              key={`inputField-${name}-${currentReturnKeyType}ReturnKey`}
              ref={focusRef}
              testID={testID ?? name}
              value={value}
              maxLength={maxCharLength}
              onChange={onChange}
              editable={!readOnly}
              onContentSizeChange={handleOnContentSizeChange}
              onFocus={focusProps.onFocus}
              onBlur={focusProps.onBlur}
              placeholder={readOnly ? undefined : placeholder}
              placeholderTextColor={placeholderTextColor || colors.inputTextPlaceholder}
              multiline={multiline}
              numberOfLines={numberOfLines} // for android and web only
              textAlignVertical={multiline ? 'top' : 'center'}
              style={[
                {
                  flexGrow: 1,
                  flexShrink: 1,
                  minWidth: 0,
                  ...(prunedTypeStyle as TextStyle),
                },
                Platform.OS === 'web' && { lineHeight: inputLineHeight },
                Platform.OS === 'android' && { padding: 0 },
                multiline && {
                  lineHeight: inputLineHeight,
                  minHeight: Platform.OS === 'web' ? contentHeight : initialContentHeight,
                  ...(Platform.OS !== 'web' && effectiveScrollEnabled && { maxHeight: initialContentHeight }),
                },
              ]}
              scrollEnabled={effectiveScrollEnabled}
              keyboardType={keyboardType}
              inputAccessoryViewID={inputAccessoryViewID}
              hitSlop={{ top: 24, left: 24, bottom: 24, right: 24 }}
              contextMenuHidden={copyPasteDisabled}
              /**
               * The context menu still appears on Samsung devices when contextMenuHidden is set to true.
               * Setting caretHidden to true fixes the problem but also hides the blinking typing indicator.
               * In order to not degrade the experience for all users we will only set this true for Samsung devices.
               */
              caretHidden={copyPasteDisabled && isSamsung}
              accessibilityLabel={accessibilityLabelProp ?? label}
              accessibilityLabelledBy={finalAccessibilityLabelledBy}
              aria-required={required}
              aria-describedby={error && errorMessageId ? errorMessageId : undefined}
              secureTextEntry={secureTextEntry}
              autoCapitalize={autoCapitalize}
              autoComplete={autoComplete as TextInputProps['autoComplete']}
              blurOnSubmit={blurOnSubmit}
              returnKeyType={currentReturnKeyType}
              textContentType={textContentType}
              importantForAutofill='yes'
              keyboardAppearance={isDarkMode ? 'dark' : undefined}
              onSubmitEditing={onSubmitEditing}
              // We only want to define onSubmitEditing prop unless passed directly for non-multiline inputs because it causes unexpected behavior for text area inputs.
              // Additionally, only defining the following props for android devices when navigateToNewField is defined since other platforms don't
              // use return key for form navigation.
              {...(!multiline &&
                Platform.OS === 'android' &&
                navigateToNewField && {
                  onSubmitEditing: () => {
                    if (currentReturnKeyType === 'next' || currentReturnKeyType === 'previous') {
                      navigateToNewField(currentReturnKeyType)
                    }
                  },
                  // prevents flickering when programmatically focusing on a new field
                  blurOnSubmit: currentReturnKeyType !== 'next' && currentReturnKeyType !== 'previous',
                })}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              nativeID={nativeID}
              {...getAuroraInputProps(isAuroraTheme)}
            />
          )}
          {children}
          {inputExtras}
          {showCharLimit && maxCharLength && (
            <CharacterCounterContainer
              text={
                <FormattedMessage
                  defaultMessage='Character limit ({count}/{length})'
                  description='Shows how many characters the user has left to use in this field'
                  values={{
                    count: value?.length ?? 0,
                    length: maxCharLength,
                  }}
                />
              }
              size={BodyTextSize.CAPTION}
            />
          )}
        </InputFieldContainer>
      </BorderComponent>
    </BaseInput>
  )
}

export const InputFieldRFF: FunctionComponent<FieldRenderProps<string>> = ({
  input: { value, onChange, onFocus, onBlur, name },
  placeholder,
  meta: { touched, error, submitError, active, dirtySinceLastSubmit },
  label,
  sublabel,
  sublabelComponent,
  multiline,
  numberOfLines,
  readOnly,
  keyboardType,
  inputAccessoryViewID,
  typeStyle,
  secureTextEntry,
  placeholderTextColor,
  autoCapitalize,
  autoComplete,
  baseInputStyle,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipSize,
  backgroundColor,
  maxCharLength,
  showCharLimit,
  accessibilityLabelledBy,
  scrollEnabled,
  testID,
  returnKeyType,
  labelStyle,
  labelContainerStyle,
  isOptionalLabel,
  inputContainerStyle,
  textContentType,
  toolTipContentStyle,
  toolTipHoverEnabled,
  showErrorAsWarning,
  handleOnInputFocus,
  handleOnInputBlur,
  getReturnKeyType,
  setInputRefs,
  navigateToNewField,
  areAllInputRefsSet,
  inputExtras,
  CustomInputFieldComponent,
  customInputFieldProps,
  labelBadgeType,
  nativeID,
  onSubmitEditing,
  aiBorder,
  customError,
  customTextStyle,
  errorAccessibilityText,
  accessibilityLabel,
  required,
  showFocusRing,
}) => {
  const InputField_: typeof InputField = CustomInputFieldComponent ?? InputField

  return (
    <InputField_
      label={label}
      sublabel={sublabel}
      subLabelComponent={sublabelComponent}
      value={value}
      onChange={(e) => onChange(e)}
      onFocus={() => {
        if (handleOnInputFocus) {
          handleOnInputFocus(name)
        }
        onFocus()
      }}
      onBlur={() => {
        if (handleOnInputBlur) {
          handleOnInputBlur(name)
        }
        onBlur()
      }}
      placeholder={placeholder}
      active={active}
      multiline={multiline}
      numberOfLines={numberOfLines}
      error={customError || (touched && (error || (!dirtySinceLastSubmit && submitError)))}
      name={name}
      readOnly={readOnly}
      keyboardType={keyboardType}
      inputAccessoryViewID={inputAccessoryViewID}
      typeStyle={typeStyle}
      secureTextEntry={secureTextEntry}
      placeholderTextColor={placeholderTextColor}
      autoCapitalize={autoCapitalize}
      autoComplete={autoComplete}
      baseInputStyle={baseInputStyle}
      toolTipContent={toolTipContent}
      onSubmitEditing={onSubmitEditing}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipSize={toolTipSize}
      backgroundColor={backgroundColor}
      maxCharLength={maxCharLength}
      showCharLimit={showCharLimit}
      accessibilityLabelledBy={accessibilityLabelledBy}
      scrollEnabled={scrollEnabled}
      testID={testID}
      returnKeyType={returnKeyType}
      labelStyle={labelStyle}
      labelContainerStyle={labelContainerStyle}
      isOptionalLabel={isOptionalLabel}
      inputContainerStyle={inputContainerStyle}
      textContentType={textContentType}
      toolTipContentStyle={toolTipContentStyle}
      toolTipHoverEnabled={toolTipHoverEnabled}
      /**
       * We only want to show the warning color after the user submits the form.
       * Using dirtySinceLastSubmit here to check if the user has updated the field
       * after submitting, so we so we can show the correct validation error
       * color or default color when needed.
       */
      showErrorAsWarning={
        customError ? showErrorAsWarning : showErrorAsWarning && !dirtySinceLastSubmit && !!submitError
      }
      setInputRefs={setInputRefs}
      areAllInputRefsSet={areAllInputRefsSet}
      getReturnKeyType={getReturnKeyType}
      navigateToNewField={navigateToNewField}
      inputExtras={inputExtras}
      labelBadgeType={labelBadgeType}
      nativeID={nativeID}
      aiBorder={aiBorder}
      customTextStyle={customTextStyle}
      errorAccessibilityText={errorAccessibilityText}
      accessibilityLabel={accessibilityLabel}
      required={required}
      showFocusRing={showFocusRing}
      {...customInputFieldProps}
    />
  )
}
